// src/components/Pentests/tabs/Audit.js
//
// Per-PT audit feed - "who did what, when". Open to both Pentester and
// Wasabian roles so the read-only viewer can see what the operator(s) have
// been doing on this engagement.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ChevronDown,
  ChevronRight,
  History,
  Pencil,
  Plus,
  RefreshCw,
  Shield,
  ShieldOff,
  Sparkles,
  Trash2,
  XCircle,
  Zap,
} from 'lucide-react';
import { pentestsAPI } from '../../../services/api';
import { EmptyState } from '../../common/Shinobi';
import { parseServerDate } from '../../../utils/time';

// Action -> { label, icon, accent }. Accent maps to Tailwind text-* tokens.
const ACTION_META = {
  'pentest.create':         { label: 'Created pen test',         icon: Plus,        accent: 'shinobi-green' },
  'pentest.update':         { label: 'Updated pen test',         icon: Pencil,      accent: 'cyan-300' },
  'pentest.delete':         { label: 'Deleted pen test',         icon: Trash2,      accent: 'red-400' },
  'finding.create':         { label: 'Added finding',            icon: Plus,        accent: 'shinobi-green' },
  'finding.update':         { label: 'Edited finding',           icon: Pencil,      accent: 'cyan-300' },
  'finding.delete':         { label: 'Deleted finding',          icon: Trash2,      accent: 'red-400' },
  'finding.mark_fp':        { label: 'Marked finding as FP',     icon: ShieldOff,   accent: 'amber-300' },
  'finding.unmark_fp':      { label: 'Restored finding from FP', icon: Shield,      accent: 'cyan-300' },
  'engagement.create':      { label: 'Started AI engagement',    icon: Sparkles,    accent: 'purple-300' },
  'engagement.approve':     { label: 'Approved AI plan',         icon: Sparkles,    accent: 'shinobi-green' },
  'engagement.stop':        { label: 'Stopped AI engagement',    icon: XCircle,     accent: 'red-400' },
  'scan.dispatch':          { label: 'Dispatched scan',          icon: Zap,         accent: 'cyan-300' },
};

function actionMeta(action) {
  return ACTION_META[action] || { label: action, icon: History, accent: 'slate-300' };
}

function relTime(d) {
  if (!d) return '';
  const diff = Date.now() - d.getTime();
  if (diff < 30 * 1000) return 'just now';
  if (diff < 60 * 1000) return `${Math.round(diff / 1000)}s ago`;
  if (diff < 60 * 60 * 1000) return `${Math.round(diff / 60000)}m ago`;
  if (diff < 24 * 60 * 60 * 1000) return `${Math.round(diff / 3600000)}h ago`;
  return d.toLocaleString();
}

// Group rows by day (YYYY-MM-DD in browser local time) so the feed reads as
// dated chunks instead of one undifferentiated stream.
function groupByDay(rows) {
  const groups = new Map();
  for (const r of rows) {
    const d = parseServerDate(r.created_at);
    if (!d) continue;
    const key = d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push({ ...r, _date: d });
  }
  return Array.from(groups.entries());
}

// Initials for the avatar pill - takes "Milot Shala" -> "MS", falls back to
// the first two letters of the email/username for users without full_name.
function initials(label) {
  if (!label) return '??';
  const parts = label.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return label.slice(0, 2).toUpperCase();
}

// Stable color mapped from the actor's label hash. Gives every user a
// recognizable avatar tint without needing real avatar images.
function actorAccent(label) {
  const palette = [
    'bg-purple-100 text-purple-700 border-purple-300',
    'bg-cyan-100 text-cyan-700 border-cyan-300',
    'bg-pink-100 text-pink-700 border-pink-300',
    'bg-amber-100 text-amber-700 border-amber-300',
    'bg-wasabi-100 text-wasabi-700 border-wasabi-300',
    'bg-rose-100 text-rose-700 border-rose-300',
    'bg-sky-100 text-sky-700 border-sky-300',
  ];
  let h = 0;
  for (let i = 0; i < (label || '').length; i++) h = (h * 31 + label.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
}

function PayloadView({ payload }) {
  if (!payload || Object.keys(payload).length === 0) {
    return <div className="text-xs text-slate-500 italic">No additional details</div>;
  }
  return (
    <pre className="text-[11px] font-mono text-slate-700 bg-cyber-100/40 border border-cyber-300/30 rounded-md p-2.5 overflow-x-auto">
      {JSON.stringify(payload, null, 2)}
    </pre>
  );
}

function EventRow({ row, expanded, onToggle }) {
  const meta = actionMeta(row.action);
  const Icon = meta.icon;
  const ts = row._date;
  const refLabel = row.entity_id != null ? `${row.entity_type}#${row.entity_id}` : row.entity_type;
  return (
    <div className="border-l-2 border-cyber-300/30 pl-4 pb-3 relative">
      <span className={`absolute -left-[5px] top-1.5 w-2 h-2 rounded-full bg-${meta.accent}`} />
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left flex items-start gap-3 group"
      >
        <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full border text-[10px] font-bold tracking-wider ${actorAccent(row.actor_label)}`}>
          {initials(row.actor_label)}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-2">
            <span className="text-sm font-semibold text-slate-900">{row.actor_label}</span>
            <Icon className={`w-3.5 h-3.5 text-${meta.accent}`} />
            <span className={`text-sm text-${meta.accent}`}>{meta.label}</span>
            <span className="text-xs text-slate-500 font-mono">{refLabel}</span>
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {relTime(ts)} · {ts ? ts.toLocaleString() : ''}
          </div>
        </div>
        {row.payload && Object.keys(row.payload).length > 0 && (
          expanded
            ? <ChevronDown className="w-4 h-4 text-slate-500 group-hover:text-slate-700" />
            : <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-slate-700" />
        )}
      </button>
      {expanded && (
        <div className="mt-2 ml-10">
          <PayloadView payload={row.payload} />
        </div>
      )}
    </div>
  );
}

export default function Audit() {
  const { id: pentestIdParam } = useParams();
  const pentestId = Number(pentestIdParam);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(new Set());

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const data = await pentestsAPI.auditLog.list(pentestId, { limit: 500 });
      setRows(data.rows || []);
      setTotal(data.total || 0);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [pentestId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const groups = useMemo(() => groupByDay(rows), [rows]);

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  if (loading) return <div className="text-slate-500">Loading audit feed...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-lg font-semibold text-slate-900">
          Activity
          <span className="ml-2 text-xs text-slate-500 font-normal">
            {total} {total === 1 ? 'event' : 'events'} on this pen test
          </span>
        </h2>
        <button
          type="button"
          onClick={fetchAll}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border bg-cyber-100 border-cyber-300/60 text-slate-700 hover:border-cyber-300 transition"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </button>
      </div>

      {error && <div className="text-red-600 text-sm">{error}</div>}

      {rows.length === 0 ? (
        <EmptyState
          icon={History}
          accent="purple"
          title="No activity yet"
          description="Once someone creates a finding, runs a scan, or kicks off an AI engagement, it will show up here."
        />
      ) : (
        <div className="rounded-2xl border border-cyber-300/40 bg-cyber-50/60 p-5 space-y-6">
          {groups.map(([day, dayRows]) => (
            <div key={day}>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-3 sticky top-0 bg-cyber-50/95 backdrop-blur py-1">
                {day}
              </div>
              <div className="space-y-1">
                {dayRows.map((r) => (
                  <EventRow
                    key={r.id}
                    row={r}
                    expanded={expanded.has(r.id)}
                    onToggle={() => toggle(r.id)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
